import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { avisoDePrivacidadeEm } from '@mpa/shared/privacidade';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { CamposLocalidade } from '../components/CamposLocalidade';
import { Campo, Carregando, ErroBox, Form, LinhaCampos, Secao } from '../components/ui';
import { limpar } from './Eventos';

/**
 * Os dados do cliente, editados pelo próprio cliente.
 *
 * Até aqui só o Superadmin mudava nome fantasia, razão social e contato, pelo
 * painel Master. Para o cliente, isso significava abrir um chamado para
 * corrigir o próprio nome — e o nome aparece na barra lateral de todo mundo da
 * equipe, então o erro ficava à vista o dia inteiro.
 *
 * O que NÃO está aqui é tão deliberado quanto o que está: status, limites de
 * plano, validade do contrato e notas internas continuam só no Master. São o
 * que a plataforma anota SOBRE o cliente; mostrá-los em modo leitura só
 * convidaria a pergunta "por que não consigo mudar?".
 */
/**
 * O Aviso da plataforma, no domínio em que o painel está rodando.
 *
 * `window.location.origin` e não uma constante: em desenvolvimento isto vira
 * `http://localhost:5173/privacidade`, que é o endereço certo para quem está
 * testando. A API monta o mesmo a partir de `WEB_URL` — ver
 * `avisoDePrivacidadePadrao` em `journeys.service.ts`.
 */
const avisoPadrao = avisoDePrivacidadeEm(window.location.origin);

interface Organizacao {
  id: string;
  legalName: string;
  tradeName: string;
  segment: string | null;
  privacyNoticeUrl: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
}

export function ConfigOrganizacao() {
  const cliente = useQueryClient();
  const { recarregar } = useAuth();
  const [salvo, setSalvo] = useState(false);

  const consulta = useQuery({
    queryKey: ['minha-organizacao'],
    queryFn: () => api<Organizacao>('/minha-organizacao'),
  });

  const salvar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api('/minha-organizacao', { method: 'PATCH', body: limpar(dados) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['minha-organizacao'] });
      // O nome fantasia aparece na barra lateral, e vem do `/auth/me` — que não
      // passa pelo TanStack Query. Sem esta releitura, salvar mudava o
      // formulário e deixava o nome antigo no canto até um F5, e a pessoa saía
      // achando que não tinha salvo.
      void recarregar();
      setSalvo(true);
      setTimeout(() => setSalvo(false), 3000);
    },
  });

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  const org = consulta.data;

  return (
    <Secao
      titulo="Organização"
      descricao="Os dados da sua empresa. O nome fantasia é o que aparece para a sua equipe no painel."
    >
      <div className="card">
        <Form aoEnviar={(dados) => salvar.mutate(dados)}>
          {salvar.isError && <div className="form-erro">{(salvar.error as Error).message}</div>}

          <LinhaCampos>
            <Campo
              label="Nome fantasia"
              name="tradeName"
              obrigatorio
              valorInicial={org.tradeName}
              ajuda="Como a empresa é conhecida. Aparece no painel da sua equipe."
            />
            <Campo
              label="Razão social"
              name="legalName"
              obrigatorio
              valorInicial={org.legalName}
              ajuda="O nome registrado, como está no contrato social."
            />
          </LinhaCampos>

          <Campo
            label="Segmento"
            name="segment"
            valorInicial={org.segment ?? ''}
            placeholder="Eventos corporativos, tecnologia, indústria…"
          />

          {/* Não é burocracia de cadastro: este endereço vai DENTRO da mensagem
              de consentimento que o participante lê no WhatsApp.

              Vem preenchido com o Aviso da plataforma em vez de vazio. A versão
              anterior deixava em branco e, com ele em branco, o envio do
              consentimento era recusado — o convite saía, as boas-vindas
              chegavam, e a segunda mensagem não. Um silêncio que só aparecia no
              log. Mostrar o padrão torna visível qual endereço a pessoa vai
              receber, em vez de fazer o sistema escolher por baixo. */}
          <Campo
            label="Aviso de Privacidade (endereço)"
            name="privacyNoticeUrl"
            tipo="url"
            valorInicial={org.privacyNoticeUrl ?? avisoPadrao}
            placeholder={avisoPadrao}
            ajuda="Aparece na mensagem de consentimento enviada ao participante. Já vem com o Aviso da MatériaPrimAi, que serve para eventos operados por ela. Se a sua empresa tem um aviso próprio, troque pelo endereço dele."
          />

          {/* O mesmo componente do cadastro de evento e do painel Master: com
              Brasil, Estado e Cidade viram listas; fora dele, texto livre. */}
          <CamposLocalidade
            paisInicial={org.country ?? 'BR'}
            estadoInicial={org.state ?? ''}
            cidadeInicial={org.city ?? ''}
          />

          <div className="divider" style={{ margin: '18px 0' }} />

          {/* Contato da organização, não do usuário logado: é para quem
              administra a conta falar com a MatériaPrimAi, e vice-versa. */}
          <LinhaCampos>
            <Campo
              label="Nome do contato"
              name="contactName"
              valorInicial={org.contactName ?? ''}
            />
            <Campo
              label="E-mail do contato"
              name="contactEmail"
              tipo="email"
              valorInicial={org.contactEmail ?? ''}
            />
          </LinhaCampos>

          <Campo
            label="Telefone do contato"
            name="contactPhone"
            valorInicial={org.contactPhone ?? ''}
            placeholder="+55 47 90000-0000"
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 20,
            }}
          >
            {/* Confirmação some sozinha: aviso permanente de sucesso vira
                paisagem, e na visita seguinte ninguém sabe se é desta vez. */}
            {salvo && (
              <span style={{ color: 'var(--success)', fontSize: 13 }}>
                <i className="ti ti-check" /> Alterações salvas
              </span>
            )}
            <button className="btn btn-primary" type="submit" disabled={salvar.isPending}>
              {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </Form>
      </div>

      <div className="aviso" style={{ marginTop: 16 }}>
        <i className="ti ti-info-circle" />
        <span>
          Plano, limites e situação do contrato são administrados pela MatériaPrimAi e não
          aparecem aqui. Para tratar deles, fale com o seu contato.
        </span>
      </div>
    </Secao>
  );
}
